import '../GraphSidePanel.css';

import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { DeviceGroupAttribute } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { isTopologyGraphMetadata } from 'components/Graph/GraphState';
import { savePathDefaultLayout } from 'components/Graph/commands/savePathDefaultLayout';
import { memo, useEffect } from 'react';
import type { FC } from 'react';
import { GraphNetworkViewerDataInputForm } from './GraphNetworkViewerDataInputForm';
import {
  GraphNetworkViewerDataInputLayout,
  type SaveLayoutButtonClickEvent,
} from './GraphNetworkViewerDataInputLayout';
import type { NetworkViewerDataInputHook } from './useNetworkViewerDataInput';

interface Props {
  networkViewerDataInputHook: NetworkViewerDataInputHook;
}

const BareGraphNetworkViewerDataInput: FC<Props> = (props) => {
  const [savingLayoutState, setSavingLayoutState] = useLoadingState();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    topologyMetadata: (s) =>
      isTopologyGraphMetadata(s.graphMetadata) ? s.graphMetadata : null,
  });

  const {
    formik,
    loadingState,
    pathsInfiniteScroll,
    setPathFilter,
    pathLayoutEdit,
    setPathLayoutEdit,
  } = props.networkViewerDataInputHook;

  const handleGroupByChange = (newGroupBy: DeviceGroupAttribute) => {
    setPathFilter('');
    formik.setFieldValue('pathFilter', '');
    formik.setFieldValue(
      'paths',
      new Set(
        newGroupBy === ctxProps.topologyMetadata?.parameters.groupBy
          ? ctxProps.topologyMetadata.parameters.paths
          : [],
      ),
    );
    formik.setFieldTouched('paths', false);
  };

  const handleLayoutBackButtonClick = useStableCallback(() => {
    setPathLayoutEdit(null);
  });

  const handleSaveLayoutButtonClick = useStableCallback(
    async (event: SaveLayoutButtonClickEvent) => {
      await withLoadingIndicator(setSavingLayoutState)(async () =>
        ctxProps.dispatch(savePathDefaultLayout, {
          groupBy: event.groupBy,
          layout: event.layout,
          path: event.path,
        }),
      );
      setPathLayoutEdit(null);
    },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: final cleanup
  useEffect(() => {
    return () => {
      setPathLayoutEdit(null);
    };
  }, []);

  return (
    <div className='flex flex-1 flex-col'>
      {pathLayoutEdit === null ? (
        <GraphNetworkViewerDataInputForm
          formik={formik}
          loadingState={loadingState}
          onGroupByChange={handleGroupByChange}
          onLayoutEditClick={setPathLayoutEdit}
          paths={pathsInfiniteScroll.items}
          pathsScrollPaginatorProps={pathsInfiniteScroll.scrollPaginatorProps}
        />
      ) : (
        <GraphNetworkViewerDataInputLayout
          groupBy={formik.values.groupBy!}
          onBackButtonClick={handleLayoutBackButtonClick}
          onSaveLayoutButtonClick={handleSaveLayoutButtonClick}
          path={pathLayoutEdit}
          saving={savingLayoutState === 'LOADING'}
        />
      )}
    </div>
  );
};

export const GraphNetworkViewerDataInput = memo(
  BareGraphNetworkViewerDataInput,
);
